import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Database, AlertTriangle, Building2 } from 'lucide-react';
import { OVERVIEW_LABELS } from '../stepCatalog';
import type { TenantOverview as TenantOverviewModel } from '../types';

interface TenantOverviewProps {
  overview: TenantOverviewModel | null;
  loading: boolean;
}

/**
 * Vue de l'état actuel du tenant V2 : compteurs par dimension. Aide à décider
 * ce qu'il reste à importer (l'assistant est incrémental).
 */
export function TenantOverview({ overview, loading }: TenantOverviewProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
        Lecture du contenu de l’école…
      </div>
    );
  }

  if (!overview) {
    return null;
  }

  const entries = Object.entries(OVERVIEW_LABELS).filter(([key]) => key in overview.counts);
  const establishments = overview.establishments ?? [];
  const admEstablishments = overview.school?.establishmentsCount ?? 0;

  return (
    <Card>
      <CardHeader icon={<Database />} iconColor="teal">
        <CardTitle className="text-base">Contenu actuel de l’école</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Ce qui existe déjà dans l’école. Vous pouvez n’importer que ce qui manque — les éléments
          déjà présents seront réutilisés automatiquement.
        </p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {entries.map(([key, label]) => (
            <div key={key} className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-2xl font-bold">{overview.counts[key]}</p>
            </div>
          ))}
        </div>

        {establishments.length > 0 && (
          <div className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              Établissements de l’école
            </p>
            <div className="flex flex-wrap gap-2">
              {establishments.map((e, i) => (
                <Badge key={e.id ?? e.name} variant="secondary">
                  {e.name || 'Sans nom'}
                  {e.rne ? ` · ${e.rne}` : ''}
                  {i === 0 && establishments.length > 1 ? ' (par défaut)' : ''}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* L'import V2 rattache toutes les classes créées à l'établissement
            par défaut, sans correction possible ensuite via l'API V2. */}
        {establishments.length > 1 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Cette école a plusieurs établissements : les classes créées par l’import seront
              toutes rattachées à l’établissement par défaut («{' '}
              {establishments[0]?.name ?? 'le plus ancien'} »), et ce rattachement n’est pas
              modifiable ensuite. Si les classes doivent être réparties par établissement,
              faites-le côté V2 avant de créer des données qui en dépendent.
            </AlertDescription>
          </Alert>
        )}
        {admEstablishments > 1 && establishments.length <= 1 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {admEstablishments} établissements sont déclarés côté ADM mais l’école V2 n’en a
              qu’un : synchronisez-les depuis l’onglet « Établissements » de la fiche école avant
              d’importer si nécessaire.
            </AlertDescription>
          </Alert>
        )}

        {overview.warnings.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{overview.warnings.join(' · ')}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
